import { useState, useEffect, useRef, useCallback } from 'react'

interface UseLazyLoadImageOptions {
    rootMargin?: string
    threshold?: number
    enabled?: boolean
}

/**
 * Hook for lazy loading images when they enter the viewport
 */
export function useImageLazyLoading<T extends HTMLElement = HTMLDivElement>({
    rootMargin = '200px 0px',
    threshold = 0.1,
    enabled = true,
}: UseLazyLoadImageOptions = {}) {
    const [isVisible, setIsVisible] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const ref = useRef<T | null>(null)

    // Mark as loaded when image is loaded
    const onLoad = useCallback(() => {
        setIsLoaded(true)
    }, [])

    useEffect(() => {
        // If not enabled, mark as visible immediately
        if (!enabled) {
            setIsVisible(true)
            return
        }

        const element = ref.current
        if (!element) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    observer.disconnect()
                }
            },
            { rootMargin, threshold }
        )

        observer.observe(element)

        return () => {
            observer.disconnect()
        }
    }, [rootMargin, threshold, enabled])

    return { ref, isVisible, isLoaded, onLoad }
}

export default useImageLazyLoading